import {
  meetingAfter as sharedMeetingAfter,
  nextMeetingDate as sharedNextMeetingDate,
  previousBusinessDay,
} from "@spectree/shared";

import type { GroomingMeetingPattern } from "@/lib/api/grooming";
import { addDays, dateParts, localToday } from "@/lib/grooming/agenda-run";

/**
 * Meeting date arithmetic in the web (PKG-PM-C, ER-1638, feature
 * PI-SPEC-SW-1490), on the same arithmetic as the API (`@spectree/shared`
 * meeting-time.ts, PKG-PM-F),
 * so a screen can say which meeting an action is for before it is pressed
 * ("to the agenda for Monday, Nov 9", frame C2). The server's answer (the
 * `targetDate` of `POST /batches/:id/next-agenda`) is authoritative; these
 * are previews. Dates are `YYYY-MM-DD` calendar dates; the arithmetic counts
 * whole calendar days, never hours, so a daylight saving change never moves a
 * meeting. Pure.
 */

/** `YYYY-MM-DD` of an instant as a calendar date in an IANA time zone; the browser's own day if the zone is unknown. */
export function localDateIn(timeZone: string, at: Date): string {
  try {
    // en-CA formats a date as YYYY-MM-DD.
    return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(at);
  } catch {
    return localToday(at);
  }
}

/**
 * The next meeting on or after `now`'s date in the pattern's time zone, as the
 * API's `nextMeetingDate` (the shared arithmetic in `@spectree/shared`
 * meeting-time.ts). An unknown zone counts from the browser's own day.
 */
export function nextMeetingDate(pattern: GroomingMeetingPattern, now: Date = new Date()): string {
  try {
    return sharedNextMeetingDate(pattern, now);
  } catch {
    return sharedNextMeetingDate({ ...pattern, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }, now);
  }
}

/**
 * The first meeting date strictly after `date` (01-roles.md §9.6: a
 * carried-over or new agenda is for the meeting after the one the run was
 * prepared for). Before the pattern's first meeting, that first meeting.
 *
 * @example meetingAfter({ weekday: 1, everyNWeeks: 2, anchorDate: "2026-10-12", timeZone: "America/Chicago" },
 *   "2026-10-26") === "2026-11-09"
 */
export function meetingAfter(pattern: GroomingMeetingPattern, date: string): string {
  return sharedMeetingAfter(pattern, date);
}

/**
 * Whether `date` is a meeting day of the pattern (PKG-PM-C Mac walk,
 * 2026-10-23): Session's "Changed for this session" chip is for a date the
 * schedule doesn't give, not for any meeting other than the next one.
 */
export function isMeetingDate(pattern: GroomingMeetingPattern, date: string): boolean {
  return meetingAfter(pattern, addDays(date, -1)) === date;
}

export interface PreparedRun {
  sessionDate?: string | null;
  startedAt: string | null;
  createdAt: string;
}

/**
 * The meeting a prepared agenda run was for: its own `sessionDate` (stamped
 * at start since PKG-PM-C), else the meeting that was next when it started.
 * Null without either.
 */
export function preparedForDate(run: PreparedRun, pattern: GroomingMeetingPattern | null): string | null {
  if (run.sessionDate) return run.sessionDate;
  if (!pattern) return null;
  return nextMeetingDate(pattern, new Date(run.startedAt ?? run.createdAt));
}

/**
 * The meeting a carry-over from `run` goes to: the meeting after the one it
 * was prepared for, never before the project's next meeting (the API's
 * `targetMeetingDate`). Null without a meeting pattern.
 */
export function carryOverDate(run: PreparedRun, pattern: GroomingMeetingPattern | null, now: Date = new Date()): string | null {
  if (!pattern) return null;
  const preparedFor = preparedForDate(run, pattern);
  if (!preparedFor) return null;
  const after = meetingAfter(pattern, preparedFor);
  const upcoming = nextMeetingDate(pattern, now);
  return after < upcoming ? upcoming : after;
}

/** `2026-11-09` → "Monday, Nov 9". */
export function formatMeetingDate(date: string): string {
  return dateParts(date).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
}

/**
 * A meeting in a picker (E323, the SessionPicker): short weekday, short month
 * and day, no comma.
 *
 * @example shortMeetingDate("2026-11-11") === "Wed Nov 11"
 */
export function shortMeetingDate(date: string): string {
  const day = dateParts(date);
  const weekday = day.toLocaleDateString("en-US", { weekday: "short" });
  const monthDay = day.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${weekday} ${monthDay}`;
}

/**
 * The Backlog's session label (E323, PI-SPEC-SW-1738): the upcoming meeting
 * an item's agenda is for, as en-US short month and day with no weekday, or
 * "In session" when the agenda has no date (the project has no meeting
 * pattern).
 *
 * @example sessionLabel("2026-11-25") === "In Nov 25 session"
 * @example sessionLabel(null) === "In session"
 */
export function sessionLabel(sessionDate: string | null): string {
  if (!sessionDate) return "In session";
  const day = dateParts(sessionDate).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `In ${day} session`;
}

/**
 * A meeting's short month and day for toasts (E323, PI-SPEC-SW-1739):
 * "Removed from the Nov 11 session".
 *
 * @example meetingMonthDay("2026-11-11") === "Nov 11"
 */
export function meetingMonthDay(date: string): string {
  return dateParts(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Whether an agenda's meeting has passed, so the agenda is a read-only record
 * (E323, ER-1710, owner decision 1): its date is before today in the pattern's
 * time zone (UTC without a pattern), the API's `isAgendaLocked`. A missing date
 * never locks. Pure.
 *
 * @example isPastMeeting("2026-11-02", null, new Date("2026-11-03T00:00:01Z")) === true
 */
export function isPastMeeting(
  meetingDate: string | null,
  pattern: Pick<GroomingMeetingPattern, "timeZone"> | null,
  now: Date = new Date()
): boolean {
  return meetingDate !== null && meetingDate < localDateIn(pattern?.timeZone ?? "UTC", now);
}

// ---------------------------------------------------------------------------
// When to prepare a meeting (PKG-PM-C, ER-1638, feature PI-SPEC-SW-1492, frame C3)
// ---------------------------------------------------------------------------

/**
 * The day to aim to prepare a meeting by: the business day before it
 * (01-roles.md §9.3: ideally one business day before, meetings are often on a
 * Monday; never forced). Steps back one calendar day, then past Saturday and
 * Sunday. No holiday calendar. Pure; whole calendar days, so daylight saving
 * never moves it. The one copy is `previousBusinessDay` in `@spectree/shared`
 * (PKG-PM-F, feature PI-SPEC-SW-1507), which the API uses too.
 *
 * @example prepareByDate("2026-10-26") === "2026-10-23" // Monday → Friday
 */
export const prepareByDate: (meetingDate: string) => string = previousBusinessDay;

/** Whether the prepare-by day is still ahead (grey hint) or has come (amber hint, from that day until the meeting). */
export type PrepareByTone = "ahead" | "due";

/** `"due"` when `today` (`YYYY-MM-DD`) is on or after `prepareBy`, else `"ahead"`. A hint only: it never blocks Prepare. */
export function prepareByTone(today: string, prepareBy: string): PrepareByTone {
  return today >= prepareBy ? "due" : "ahead";
}

/** `2026-10-26` → "Aim to prepare by Friday, Oct 23" (Session's header, frame C3). */
export function prepareByText(meetingDate: string): string {
  return `Aim to prepare by ${formatMeetingDate(prepareByDate(meetingDate))}`;
}
